import { appConfig } from "../config";
import {
  ANALYTICS_EVENT_NAMES,
  ApiProtocolError,
  apiClient,
  type AnalyticsEventInput,
  type AnalyticsEventName,
  type ApiClient,
  type PublicAnalyticsMetadata,
} from "../integrations/api";
import { ga4, type Ga4Adapter, type Ga4TrackResult } from "../integrations/ga4";
import { metaPixel, type MetaPixelAdapter, type MetaTrackResult } from "../integrations/meta";
import {
  consentManager,
  type ConsentManager,
  type ConsentState,
} from "../privacy/consent";

export { ANALYTICS_EVENT_NAMES };
export type { AnalyticsEventName };

const EVENT_QUEUE_STORAGE_KEY = "conan.quiz.analytics.queue.v1";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const FORBIDDEN_METADATA_PATTERN = /(answer|restriction|diet|food|health|goal|score|diagnos|profile|consideration|email|phone|whatsapp|name)/i;
const MAX_QUEUE_SIZE = 200;
const API_BATCH_SIZE = 20;
const MAX_EVENT_AGE_MS = 23 * 60 * 60 * 1_000;
const MAX_EVENT_FUTURE_SKEW_MS = 5 * 60 * 1_000;

const SAFE_REASONS = new Set([
  "navigation",
  "timeout",
  "offline",
  "visibility-hidden",
  "user-exit",
  "session-expired",
  "payment-failed",
  "unknown",
]);
const SAFE_CHECKOUT_STATUSES = new Set(["initiated", "pending", "approved", "failed", "refunded"]);
const SAFE_DEVICES = new Set(["mobile", "tablet", "desktop", "unknown"]);
const SAFE_CHANNELS = new Set(["onsite", "email"]);

export interface AnalyticsEventOptions {
  eventId?: string;
  pagePath?: string;
  occurredAt?: string | Date;
  metadata?: Record<string, unknown>;
  /**
   * Disable only the authenticated first-party delivery while keeping the
   * consent-aware Meta/GA adapters active. This is used when the server owns
   * the canonical event (for example QuizComplete) or before a session exists.
   */
  firstParty?: boolean;
}

export interface AnalyticsSession {
  sessionId: string;
  sessionToken: string;
}

interface StoredQueue {
  version: 1;
  sessionId?: string;
  events: AnalyticsEventInput[];
}

export type QueueFlushStatus =
  | "empty"
  | "sent"
  | "waiting-session"
  | "waiting-consent"
  | "waiting-network"
  | "failed";

export interface QueueFlushResult {
  status: QueueFlushStatus;
  sent: number;
  pending: number;
  errorCode?: string;
}

export interface QueueEnqueueResult {
  status: "queued";
  pending: number;
  dropped: number;
}

export interface ResilientEventQueueOptions {
  api?: ApiClient;
  consent?: ConsentManager;
  storage?: Storage;
  now?: () => number;
  online?: () => boolean;
  autoFlush?: boolean;
  setTimeout?: (handler: () => void, timeout: number) => ReturnType<typeof globalThis.setTimeout>;
  clearTimeout?: (handle: ReturnType<typeof globalThis.setTimeout>) => void;
}

export type FirstPartyTrackStatus = "queued" | "blocked-consent" | "skipped";

export interface AnalyticsTrackResult {
  event: AnalyticsEventInput;
  firstParty: FirstPartyTrackStatus;
  meta: MetaTrackResult;
  ga4: Ga4TrackResult;
}

export interface AnalyticsClientOptions {
  queue?: ResilientEventQueue;
  consent?: ConsentManager;
  meta?: MetaPixelAdapter;
  ga4?: Ga4Adapter;
}

export interface AnalyticsDestinations {
  firstParty?: boolean;
  meta?: boolean;
  ga4?: boolean;
}

function availableStorage(provided?: Storage): Storage | undefined {
  if (provided) return provided;
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function createEventId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function safePagePath(value: string | undefined): string | undefined {
  if (!value) return undefined;
  try {
    const base = typeof window === "undefined" ? "http://localhost" : window.location.origin;
    const url = new URL(value, base);
    const path = url.pathname
      .replace(/^\/b\/quiz\/resultado\/[^/]+(?=\/|$)/, "/b/quiz/resultado/:token")
      .replace(/^\/quiz\/resultado\/[^/]+(?=\/|$)/, "/quiz/resultado/:token")
      .replace(/^\/oferta\/[^/]+(?=\/|$)/, "/oferta/:token");
    return path.slice(0, 180) || "/";
  } catch {
    return undefined;
  }
}

export function sanitizeAnalyticsMetadata(value: unknown): PublicAnalyticsMetadata {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const input = value as Record<string, unknown>;
  const output: PublicAnalyticsMetadata = {};
  for (const [key, item] of Object.entries(input)) {
    if (FORBIDDEN_METADATA_PATTERN.test(key)) continue;
    if (key === "questionId" && typeof item === "string" && /^q(?:1[0-4]|[1-9])$/.test(item)) {
      output.questionId = item;
    }
    if ((key === "step" || key === "total") && typeof item === "number" && Number.isInteger(item) && item >= 0 && item <= 100) {
      output[key] = item;
    }
    if (key === "device" && typeof item === "string" && SAFE_DEVICES.has(item)) output.device = item;
    if (key === "variant" && typeof item === "string" && /^(?:A|B)$/.test(item)) output.variant = item;
    if (key === "channel" && typeof item === "string" && SAFE_CHANNELS.has(item)) output.channel = item;
    if (key === "messageStep" && typeof item === "number" && Number.isInteger(item) && item >= 0 && item <= 4) {
      output.messageStep = item;
    }
    if (key === "contentVersion" && typeof item === "string" && /^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/.test(item)) {
      output.contentVersion = item;
    }
    if (key === "checkoutStatus" && typeof item === "string" && SAFE_CHECKOUT_STATUSES.has(item)) {
      output.checkoutStatus = item;
    }
    if (key === "valueMinor" && typeof item === "number" && Number.isSafeInteger(item) && item >= 0) {
      output.valueMinor = item;
    }
    if (key === "currency" && typeof item === "string" && /^[A-Za-z]{3}$/.test(item)) {
      output.currency = item.toUpperCase();
    }
    if (key === "reason" && typeof item === "string" && SAFE_REASONS.has(item)) output.reason = item;
  }
  return output;
}

export function createAnalyticsEvent(
  eventName: AnalyticsEventName,
  options: AnalyticsEventOptions = {},
): AnalyticsEventInput {
  if (!ANALYTICS_EVENT_NAMES.includes(eventName)) throw new TypeError("Unsupported analytics event name");
  const eventId = options.eventId ?? createEventId();
  if (!UUID_PATTERN.test(eventId)) throw new TypeError("Analytics eventId must be an RFC 4122 UUID v4");
  const occurredAt = options.occurredAt instanceof Date
    ? options.occurredAt.toISOString()
    : options.occurredAt
      ? new Date(options.occurredAt).toISOString()
      : new Date().toISOString();
  const pagePath = safePagePath(options.pagePath);
  const metadata = sanitizeAnalyticsMetadata(options.metadata);
  return {
    eventId,
    eventName,
    occurredAt,
    ...(pagePath ? { pagePath } : {}),
    ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
  };
}

function validStoredEvent(value: unknown): AnalyticsEventInput | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const object = value as Record<string, unknown>;
  if (
    typeof object.eventId !== "string"
    || !UUID_PATTERN.test(object.eventId)
    || typeof object.eventName !== "string"
    || !ANALYTICS_EVENT_NAMES.includes(object.eventName as AnalyticsEventName)
  ) return undefined;
  try {
    return createAnalyticsEvent(object.eventName as AnalyticsEventName, {
      eventId: object.eventId,
      ...(typeof object.pagePath === "string" ? { pagePath: object.pagePath } : {}),
      ...(typeof object.occurredAt === "string" ? { occurredAt: object.occurredAt } : {}),
      ...(object.metadata && typeof object.metadata === "object"
        ? { metadata: object.metadata as Record<string, unknown> }
        : {}),
    });
  } catch {
    return undefined;
  }
}

export class ResilientEventQueue {
  private readonly api: ApiClient;
  private readonly consent: ConsentManager;
  private readonly storage: Storage | undefined;
  private readonly now: () => number;
  private readonly online: () => boolean;
  private readonly autoFlush: boolean;
  private readonly scheduleTimeout: (handler: () => void, timeout: number) => ReturnType<typeof globalThis.setTimeout>;
  private readonly cancelTimeout: (handle: ReturnType<typeof globalThis.setTimeout>) => void;
  private events: AnalyticsEventInput[] = [];
  private session: AnalyticsSession | undefined;
  private boundSessionId: string | undefined;
  private retryAttempt = 0;
  private retryTimer: ReturnType<typeof globalThis.setTimeout> | undefined;
  private flushPromise: Promise<QueueFlushResult> | undefined;
  private readonly removeConsentListener: () => void;
  private readonly onlineListener: () => void;
  private readonly pageHideListener: () => void;

  constructor(options: ResilientEventQueueOptions = {}) {
    this.api = options.api ?? apiClient;
    this.consent = options.consent ?? consentManager;
    this.storage = availableStorage(options.storage);
    this.now = options.now ?? (() => Date.now());
    this.online = options.online ?? (() => typeof navigator === "undefined" || navigator.onLine !== false);
    this.autoFlush = options.autoFlush ?? true;
    this.scheduleTimeout = options.setTimeout ?? ((handler, timeout) => globalThis.setTimeout(handler, timeout));
    this.cancelTimeout = options.clearTimeout ?? ((handle) => globalThis.clearTimeout(handle));
    this.restore();
    this.removeConsentListener = this.consent.subscribe((state) => {
      if (!this.consent.allowsAnalytics(state)) this.clear();
      else this.scheduleFlush(0);
    });
    this.onlineListener = () => this.scheduleFlush(0);
    this.pageHideListener = () => { void this.flush({ keepalive: true }); };
    if (this.autoFlush && typeof window !== "undefined") {
      window.addEventListener("online", this.onlineListener);
      window.addEventListener("pagehide", this.pageHideListener);
    }
  }

  setSession(session: AnalyticsSession | undefined): void {
    if (!session) {
      this.session = undefined;
      return;
    }
    if (!session.sessionId.trim() || !session.sessionToken.trim()) throw new TypeError("Analytics session credentials are required");
    if (this.boundSessionId && this.boundSessionId !== session.sessionId) {
      this.events = [];
      this.retryAttempt = 0;
    }
    this.session = { ...session };
    this.boundSessionId = session.sessionId;
    this.persist();
    if (this.flushPromise) {
      void this.flushPromise.then(() => this.scheduleFlush(0));
    } else {
      this.scheduleFlush(0);
    }
  }

  enqueue(event: AnalyticsEventInput): QueueEnqueueResult {
    const sanitized = validStoredEvent(event);
    if (!sanitized) throw new TypeError("Invalid analytics event");
    if (this.events.some((candidate) => candidate.eventId === sanitized.eventId)) {
      return { status: "queued", pending: this.events.length, dropped: 0 };
    }
    this.events.push(sanitized);
    const dropped = Math.max(0, this.events.length - MAX_QUEUE_SIZE);
    if (dropped > 0) this.events.splice(0, dropped);
    this.persist();
    this.scheduleFlush(0);
    return { status: "queued", pending: this.events.length, dropped };
  }

  pendingCount(): number {
    return this.events.length;
  }

  clear(): void {
    this.events = [];
    this.retryAttempt = 0;
    if (this.retryTimer !== undefined) {
      this.cancelTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
    this.persist();
  }

  flush(options: { keepalive?: boolean } = {}): Promise<QueueFlushResult> {
    if (this.flushPromise) return this.flushPromise;
    this.flushPromise = this.performFlush(options).finally(() => {
      this.flushPromise = undefined;
    });
    return this.flushPromise;
  }

  destroy(): void {
    this.removeConsentListener();
    if (this.retryTimer !== undefined) this.cancelTimeout(this.retryTimer);
    if (this.autoFlush && typeof window !== "undefined") {
      window.removeEventListener("online", this.onlineListener);
      window.removeEventListener("pagehide", this.pageHideListener);
    }
  }

  private async performFlush(options: { keepalive?: boolean }): Promise<QueueFlushResult> {
    this.pruneStaleEvents();
    if (this.events.length === 0) return { status: "empty", sent: 0, pending: 0 };
    if (!this.consent.allowsAnalytics()) {
      this.clear();
      return { status: "waiting-consent", sent: 0, pending: 0 };
    }
    if (!this.session) return { status: "waiting-session", sent: 0, pending: this.events.length };
    if (!this.online()) {
      this.scheduleRetry();
      return { status: "waiting-network", sent: 0, pending: this.events.length };
    }

    let sent = 0;
    const activeSession = this.session;
    try {
      while (this.events.length > 0) {
        const batch = this.events.slice(0, API_BATCH_SIZE);
        const response = await this.api.ingestEvents(
          activeSession.sessionId,
          activeSession.sessionToken,
          batch,
          options.keepalive === undefined ? {} : { keepalive: options.keepalive },
        );
        if (response.accepted + response.duplicates !== batch.length) {
          throw new ApiProtocolError("/sessions/:id/events", "event acknowledgement count did not match the batch");
        }
        if (this.session?.sessionId !== activeSession.sessionId) {
          return {
            status: "failed",
            sent: sent + batch.length,
            pending: this.events.length,
            errorCode: "session_changed",
          };
        }
        this.events.splice(0, batch.length);
        sent += batch.length;
        this.persist();
      }
      this.retryAttempt = 0;
      return { status: "sent", sent, pending: 0 };
    } catch (error) {
      this.retryAttempt += 1;
      this.persist();
      this.scheduleRetry();
      const errorCode = error && typeof error === "object" && "code" in error && typeof error.code === "string"
        ? error.code
        : "delivery_failed";
      return { status: "failed", sent, pending: this.events.length, errorCode };
    }
  }

  private scheduleFlush(delay: number): void {
    if (!this.autoFlush || this.retryTimer !== undefined || this.events.length === 0) return;
    this.retryTimer = this.scheduleTimeout(() => {
      this.retryTimer = undefined;
      void this.flush();
    }, delay);
  }

  private pruneStaleEvents(): void {
    const now = this.now();
    const before = this.events.length;
    this.events = this.events.filter((event) => {
      const occurredAt = event.occurredAt ? Date.parse(event.occurredAt) : now;
      return Number.isFinite(occurredAt)
        && occurredAt >= now - MAX_EVENT_AGE_MS
        && occurredAt <= now + MAX_EVENT_FUTURE_SKEW_MS;
    });
    if (this.events.length !== before) this.persist();
  }

  private scheduleRetry(): void {
    const exponent = Math.min(this.retryAttempt, 6);
    const delay = Math.min(60_000, 1_000 * (2 ** exponent));
    this.scheduleFlush(delay);
  }

  private restore(): void {
    try {
      const raw = this.storage?.getItem(EVENT_QUEUE_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return;
      const object = parsed as Record<string, unknown>;
      if (object.version !== 1 || !Array.isArray(object.events)) return;
      this.events = object.events.map(validStoredEvent).filter((event): event is AnalyticsEventInput => Boolean(event)).slice(-MAX_QUEUE_SIZE);
      if (typeof object.sessionId === "string" && object.sessionId) this.boundSessionId = object.sessionId;
    } catch {
      this.events = [];
    }
  }

  private persist(): void {
    const payload: StoredQueue = {
      version: 1,
      events: this.events,
      ...(this.boundSessionId ? { sessionId: this.boundSessionId } : {}),
    };
    try {
      this.storage?.setItem(EVENT_QUEUE_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // The in-memory queue still retries for the current page lifecycle.
    }
  }
}

export class AnalyticsClient {
  readonly queue: ResilientEventQueue;
  private readonly consent: ConsentManager;
  private readonly meta: MetaPixelAdapter;
  private readonly ga4: Ga4Adapter;

  constructor(options: AnalyticsClientOptions = {}) {
    this.queue = options.queue ?? new ResilientEventQueue();
    this.consent = options.consent ?? consentManager;
    this.meta = options.meta ?? metaPixel;
    this.ga4 = options.ga4 ?? ga4;
  }

  setSession(session: AnalyticsSession | undefined): void {
    this.queue.setSession(session);
  }

  async track(
    eventName: AnalyticsEventName,
    options: AnalyticsEventOptions = {},
    destinations: AnalyticsDestinations = {},
  ): Promise<AnalyticsTrackResult> {
    const event = createAnalyticsEvent(eventName, options);
    const state: ConsentState = this.consent.getState();
    const firstParty = destinations.firstParty === false || options.firstParty === false
      ? "skipped"
      : this.consent.allowsAnalytics(state)
        ? this.queue.enqueue(event).status
        : "blocked-consent";
    const [meta, ga4Result] = await Promise.all([
      destinations.meta === false ? Promise.resolve({
        provider: "meta" as const,
        status: "skipped" as const,
        eventId: event.eventId,
        eventName,
      }) : this.meta.track(eventName, {
        eventId: event.eventId,
        ...(event.metadata ? { metadata: event.metadata } : {}),
        ...(event.pagePath ? { pagePath: event.pagePath } : {}),
        consent: state,
      }),
      destinations.ga4 === false ? Promise.resolve({
        provider: "ga4" as const,
        status: "skipped" as const,
        eventId: event.eventId,
        eventName,
        providerEventName: eventName,
      }) : this.ga4.track(eventName, {
        eventId: event.eventId,
        ...(event.metadata ? { metadata: event.metadata } : {}),
        ...(event.pagePath ? { pagePath: event.pagePath } : {}),
        consent: state,
      }),
    ]);
    if (appConfig.trackingDebug) {
      console.debug("[tracking:event]", {
        eventName,
        eventId: event.eventId,
        firstParty,
        meta: meta.status,
        ga4: ga4Result.status,
      });
    }
    return { event, firstParty, meta, ga4: ga4Result };
  }

  flush(options?: { keepalive?: boolean }): Promise<QueueFlushResult> {
    return this.queue.flush(options);
  }
}

export const analyticsClient = new AnalyticsClient();

export function trackAnalyticsEvent(
  eventName: AnalyticsEventName,
  options?: AnalyticsEventOptions,
): Promise<AnalyticsTrackResult> {
  return analyticsClient.track(eventName, options);
}
